import { AttendContactApplicationService } from './domain/attendContact/service/application';
import { AttendRegisterApplicationService } from './domain/attendRegister/service/application';
import { ChildApplicationService } from './domain/child/service/application';
import { ChildApplyApplicationService } from './domain/childApply/service/application';
import { ChildNotebookApplicationService } from './domain/childNotebook/service/application';
import { PageRepository } from './domain/childNotebook/service/entity/pageRepository';
import { ClassApplicationService } from './domain/class/service/application';
import { DefecationApplicationService } from './domain/defecation/service/application';
import { DeviceApplicationService } from './domain/device/service/application';
import { DrugApplicationService } from './domain/drug/service/application';
import { DrugRepository } from './domain/drug/service/entity/drugRepository';
import { FileApplicationService } from './domain/file/service/application';
import { MealApplicationService } from './domain/meal/service/application';
import { MealRepository } from './domain/meal/service/entity/mealRepository';
import { MeasureApplicationService } from './domain/measure/service/application';
import { MilkApplicationService } from './domain/milk/service/application';
import { NewsApplicationService } from './domain/news/service/application';
import { NoticeApplicationService } from './domain/notice/service/application';
import { DefecationNurseryApplicationService } from './domain/nursery/defecation/service/application';
import { DrugNurseryApplicationService } from './domain/nursery/drug/service/application';
import { MealNurseryApplicationService } from './domain/nursery/meal/service/application';
import { MeasureNurseryApplicationService } from './domain/nursery/measure/service/application';
import { MilkNurseryApplicationService } from './domain/nursery/milk/service/application';
import { SleepNurseryApplicationService } from './domain/nursery/sleep/service/application';
import { TemperatureNurseryApplicationService } from './domain/nursery/temperature/service/application';
import { PaymentApplicationService } from './domain/payment/service/application';
import { PublicApiSettingsRepository } from './domain/payment/service/entity/publicApiSettingsRepository';
import { PhotoItemRepository } from './domain/photo/service/entity/photoItemRepository';
import { PhotoSalesRepository } from './domain/photo/service/entity/photoSalesRepository';
import { PrivateBlogApplicationService } from './domain/privateBlog/service/application';
import { PrivateBlogCategoryRepository } from './domain/privateBlog/service/entity/privateBlogCategoryRepository';
import { PrivateBlogRepository } from './domain/privateBlog/service/entity/privateBlogRepository';
import { SleepApplicationService } from './domain/sleep/service/application';
import { TemperatureApplicationService } from './domain/temperature/service/application';
import { UserApplicationService } from './domain/user/service/application';
import { PaymentSquareApi } from './infra/paymentSquareApi';

import type { PageIRepository } from './domain/childNotebook/service/entity/pageIRepository';
import type { DrugIRepository } from './domain/drug/service/entity/drugIRepository';
import type { MealIRepository } from './domain/meal/service/entity/mealIRepository';
import type { PublicApiSettingsIRepository } from './domain/payment/service/entity/publicApiSettingsIRepository';
import type { PhotoItemIRepository } from './domain/photo/service/entity/photoItemIRepository';
import type { PhotoSalesIRepository } from './domain/photo/service/entity/photoSalesIRepository';
import type { PrivateBlogCategoryIRepository } from './domain/privateBlog/service/entity/privateBlogCategoryIRepository';
import type { PrivateBlogIRepository } from './domain/privateBlog/service/entity/privateBlogIRepository';
import type { AttendContactAbsenceIRepository } from '@/domain/attendContact/service/entity/attendContactAbsenceIRepository';
import type { AttendContactLateIRepository } from '@/domain/attendContact/service/entity/attendContactLateIRepository';
import type { AttendContactLeaveEarlyIRepository } from '@/domain/attendContact/service/entity/attendContactLeaveEarlyIRepository';
import type { AttendRegisterIRepository } from '@/domain/attendRegister/service/entity/attendRegisterIRepository';
import type { SheetIRepository } from '@/domain/attendance/service/entity/sheetIRepository';
import type { ChildIRepository } from '@/domain/child/service/entity/childIRepository';
import type { ChildApplyIRepository } from '@/domain/childApply/service/entity/childApplyIRepository';
import type { ChildRemoveIRepository } from '@/domain/childApply/service/entity/childRemoveIRepository';
import type { FacilityIRepository } from '@/domain/childApply/service/entity/facilityIRepository';
import type { HomepageSettingsIRepository } from '@/domain/childApply/service/entity/homepageSettingsIRepository';
import type { ClassChildIRepository } from '@/domain/class/service/entity/classChildIRepository';
import type { DefecationIRepository } from '@/domain/defecation/service/entity/defecationIRepository';
import type { DeviceIRepository } from '@/domain/device/service/entity/deviceIRepository';
import type { MeasureIRepository } from '@/domain/measure/service/entity/measureIRepository';
import type { MilkIRepository } from '@/domain/milk/service/entity/milkIRepository';
import type { SentNewsIRepository } from '@/domain/news/service/entity/sentNewsIRepository';
import type { NoticeCategoryIRepository } from '@/domain/notice/service/entity/noticeCategoryIRepository';
import type { NoticeItemIRepository } from '@/domain/notice/service/entity/noticeItemIRepository';
import type { PhotoOrderIRepository } from '@/domain/photo/service/entity/photoOrderIRepository';
import type { SleepIRepository } from '@/domain/sleep/service/entity/sleepIRepository';
import type { TemperatureIRepository } from '@/domain/temperature/service/entity/temperatureIRepository';
import type { AppSyncSubscriptionIClient } from '@/infra/appSyncSubscriptionIClient';
import type { CognitoIApi } from '@/infra/cognitoIApi';
import type { WagakoAppIApi } from '@/infra/wagakoAppIApi';
import type { WagakoNurseryIApi } from '@/infra/wagakoNurseryIApi';

import aws, { isDebug } from '@/constants/env';
import { AttendContactAbsenceRepository } from '@/domain/attendContact/service/entity/attendContactAbsenceRepository';
import { AttendContactLateRepository } from '@/domain/attendContact/service/entity/attendContactLateRepository';
import { AttendContactLeaveEarlyRepository } from '@/domain/attendContact/service/entity/attendContactLeaveEarlyRepository';
import { AttendRegisterRepository } from '@/domain/attendRegister/service/entity/attendRegisterRepository';
import { SheetRepository } from '@/domain/attendance/service/entity/sheetRepository';
import { ChildRepository } from '@/domain/child/service/entity/childRepository';
import { ChildApplyRepository } from '@/domain/childApply/service/entity/childApplyRepository';
import { ChildRemoveRepository } from '@/domain/childApply/service/entity/childRemoveRepository';
import { FacilityRepository } from '@/domain/childApply/service/entity/facilityRepository';
import { HomepageSettingsRepository } from '@/domain/childApply/service/entity/homepageSettingsRepository';
import { ClassChildRepository } from '@/domain/class/service/entity/classChildRepository';
import { DefecationRepository } from '@/domain/defecation/service/entity/defecationRepository';
import { DeviceRepository } from '@/domain/device/service/entity/deviceRepository';
import { MeasureRepository } from '@/domain/measure/service/entity/measureRepository';
import { MilkRepository } from '@/domain/milk/service/entity/milkRepository';
import { SentNewsRepository } from '@/domain/news/service/entity/sentNewsRepository';
import { NoticeCategoryRepository } from '@/domain/notice/service/entity/noticeCategoryRepository';
import { NoticeItemRepository } from '@/domain/notice/service/entity/noticeItemRepository';
import { PhotoApplicationService } from '@/domain/photo/service/application';
import { PhotoOrderRepository } from '@/domain/photo/service/entity/photoOrderRepository';
import { SleepRepository } from '@/domain/sleep/service/entity/sleepRepository';
import { TemperatureRepository } from '@/domain/temperature/service/entity/temperatureRepository';
import { AppSyncSubscriptionClient } from '@/infra/appSyncSubscriptionClient';
import { CognitoApi } from '@/infra/cognitoApi';
import { Logger, LogLevel } from '@/infra/logger';
import { WagakoAppApi } from '@/infra/wagakoAppApi';
import { WagakoNurseryApi } from '@/infra/wagakoNurseryApi';

/**
 * Api
 */
export type ApiType = {
  /** ロガー */
  logger: Logger,
  /** Cognito API */
  cognitoApi: CognitoIApi,
  /** 支払API */
  paymentApi: PaymentSquareApi,
  /** WagakoApp API */
  wagakoAppApi: WagakoAppIApi,
  /** WagakoNursery API */
  wagakoNurseryApi: WagakoNurseryIApi,
  /** サブスクリプションクライアント */
  subscriptionClient: AppSyncSubscriptionIClient,
}

/**
 * API
 */
export const api: ApiType = {
  logger: new Logger(isDebug ? LogLevel.Debug : LogLevel.Info),
  cognitoApi: new CognitoApi(),
  paymentApi: new PaymentSquareApi(),
  wagakoAppApi: new WagakoAppApi(),
  wagakoNurseryApi: new WagakoNurseryApi(),
  subscriptionClient: new AppSyncSubscriptionClient(aws.aws_appsync_graphqlEndpoint),
};

/**
 * レポジトリ
 */
export type RepositoryType = {
  /** 出席登録 */
  attendRegister: AttendRegisterIRepository,
  /** 子ども */
  child: ChildIRepository,
  /** 出席簿 */
  sheet: SheetIRepository,
  /** 欠席連絡 */
  absence: AttendContactAbsenceIRepository,
  /** 遅刻連絡 */
  late: AttendContactLateIRepository,
  /** 早退連絡 */
  leaveEarly: AttendContactLeaveEarlyIRepository,
  /** 子ども申請 */
  childApply: ChildApplyIRepository,
  /** 子ども退園 */
  childRemove: ChildRemoveIRepository,
  /** ホームページ設定 */
  homepageSettings: HomepageSettingsIRepository,
  /** 施設 */
  facility: FacilityIRepository,
  /** クラス子ども */
  classChild: ClassChildIRepository,
  /** 排泄 */
  defecation: DefecationIRepository,
  /** デバイス */
  device: DeviceIRepository,
  /** 薬 */
  drug: DrugIRepository,
  /** 測定 */
  measure: MeasureIRepository,
  /** 食事 */
  meal: MealIRepository,
  /** ミルク */
  milk: MilkIRepository,
  /** 連絡帳ページ */
  page: PageIRepository,
  /** 睡眠 */
  sleep: SleepIRepository,
  /** 写真アイテム */
  photoItem: PhotoItemIRepository,
  /** 写真注文 */
  photoOrder: PhotoOrderIRepository,
  /** 写真販売 */
  photoSales: PhotoSalesIRepository,
  /** 送信済みお知らせ */
  sentNews: SentNewsIRepository,
  /** 掲示板カテゴリ */
  noticeCategory: NoticeCategoryIRepository,
  /** 掲示板アイテム */
  noticeItem: NoticeItemIRepository,
  /** 公開API設定 */
  publicApiSettings: PublicApiSettingsIRepository,
  /** 体温 */
  temperature: TemperatureIRepository,
  /** ホームページカテゴリ */
  privateBlogCategory: PrivateBlogCategoryIRepository,
  /** 限定ページ */
  privateBlog: PrivateBlogIRepository,
}

/**
 * レポジトリ
 */
export const repository: RepositoryType = {
  attendRegister: new AttendRegisterRepository(api.cognitoApi),
  child: new ChildRepository(api.cognitoApi),
  sheet: new SheetRepository(api.cognitoApi),
  absence: new AttendContactAbsenceRepository(api.cognitoApi),
  late: new AttendContactLateRepository(api.cognitoApi),
  leaveEarly: new AttendContactLeaveEarlyRepository(api.cognitoApi),
  childApply: new ChildApplyRepository(api.cognitoApi),
  childRemove: new ChildRemoveRepository(api.cognitoApi),
  homepageSettings: new HomepageSettingsRepository(api.cognitoApi),
  facility: new FacilityRepository(api.cognitoApi),
  classChild: new ClassChildRepository(api.cognitoApi),
  defecation: new DefecationRepository(api.cognitoApi),
  device: new DeviceRepository(api.cognitoApi),
  drug: new DrugRepository(api.cognitoApi),
  measure: new MeasureRepository(api.cognitoApi),
  meal: new MealRepository(api.cognitoApi),
  milk: new MilkRepository(api.cognitoApi),
  page: new PageRepository(api.cognitoApi),
  sleep: new SleepRepository(api.cognitoApi),
  photoItem: new PhotoItemRepository(api.cognitoApi),
  photoOrder: new PhotoOrderRepository(api.cognitoApi),
  photoSales: new PhotoSalesRepository(api.cognitoApi),
  sentNews: new SentNewsRepository(api.cognitoApi),
  noticeCategory: new NoticeCategoryRepository(api.cognitoApi),
  noticeItem: new NoticeItemRepository(api.cognitoApi),
  publicApiSettings: new PublicApiSettingsRepository(api.cognitoApi),
  temperature: new TemperatureRepository(api.cognitoApi),
  privateBlogCategory: new PrivateBlogCategoryRepository(api.cognitoApi),
  privateBlog: new PrivateBlogRepository(api.cognitoApi),
};

/**
 * サービス
 */
export const service = {
  /** 支払 */
  payment: new PaymentApplicationService(),
  /** 写真 */
  photo: new PhotoApplicationService(),
  /** ファイル */
  file: new FileApplicationService(),
  /** お知らせ */
  news: new NewsApplicationService(),
  /** 掲示板 */
  notice: new NoticeApplicationService(),
  /** プライベートブログ */
  privateBlog: new PrivateBlogApplicationService(),
  /** 測定 */
  measure: new MeasureApplicationService(),
  /** 測定(施設) */
  measureNursery: new MeasureNurseryApplicationService(),
  /** 食事 */
  meal: new MealApplicationService(),
  /** 食事(施設) */
  mealNursery: new MealNurseryApplicationService(),
  /** ミルク */
  milk: new MilkApplicationService(),
  /** ミルク(施設) */
  milkNursery: new MilkNurseryApplicationService(),
  /** 睡眠 */
  sleep: new SleepApplicationService(),
  /** 睡眠(施設) */
  sleepNursery: new SleepNurseryApplicationService(),
  /** 排泄 */
  defecation: new DefecationApplicationService(),
  /** 排泄(施設) */
  defecationNursery: new DefecationNurseryApplicationService(),
  /** 薬 */
  drug: new DrugApplicationService(),
  /** 薬(施設) */
  drugNursery: new DrugNurseryApplicationService(),
  /** 子ども */
  child: new ChildApplicationService(),
  /** クラス */
  class: new ClassApplicationService(),
  /** 体温 */
  temperature: new TemperatureApplicationService(),
  /** 体温(施設) */
  temperatureNursery: new TemperatureNurseryApplicationService(),
  /** 出席連絡 */
  attendContact: new AttendContactApplicationService(),
  /** 登降園 */
  attendRegister: new AttendRegisterApplicationService(),
  /** 子ども申請 */
  childApply: new ChildApplyApplicationService(),
  /** デバイス */
  device: new DeviceApplicationService(),
  /** ユーザー */
  user: new UserApplicationService(),
  /** 連絡帳 */
  childNotebook: new ChildNotebookApplicationService(),
};

/**
 * Injection
 */
export const runInjection = () => {
  // Infra/Repository
  // ApplicationService
  service.payment.inject(
    api.wagakoNurseryApi,
    api.paymentApi,
    repository.publicApiSettings,
  );
  service.photo.inject(
    api.wagakoNurseryApi,
    repository.photoItem,
    repository.photoOrder,
    repository.photoSales,
    api.logger,
  );
  service.file.inject(api.wagakoNurseryApi);
  service.news.inject(
    api.wagakoNurseryApi,
    repository.sentNews,
    api.logger,
  );
  service.notice.inject(
    api.wagakoNurseryApi,
    repository.noticeCategory,
    repository.noticeItem,
    api.logger,
  );
  service.privateBlog.inject(
    api.wagakoNurseryApi,
    repository.privateBlogCategory,
    repository.privateBlog,
    api.logger,
  );
  service.meal.inject(
    repository.meal,
    api.logger,
  );
  service.mealNursery.inject(
    api.wagakoNurseryApi,
    api.logger,
  );
  service.measure.inject(
    repository.measure,
    api.logger,
  );
  service.measureNursery.inject(
    api.wagakoNurseryApi,
    api.logger,
  );
  service.milk.inject(
    repository.milk,
    api.logger,
  );
  service.milkNursery.inject(
    api.wagakoNurseryApi,
    api.logger,
  );
  service.defecation.inject(
    repository.defecation,
    api.logger,
  );
  service.defecationNursery.inject(
    api.wagakoNurseryApi,
    api.logger,
  );
  service.drug.inject(
    repository.drug,
    api.logger,
  );
  service.drugNursery.inject(
    api.wagakoNurseryApi,
    api.logger,
  );
  service.sleep.inject(
    repository.sleep,
    api.logger,
  );
  service.sleepNursery.inject(
    api.wagakoNurseryApi,
    api.logger,
  );
  service.child.inject(
    repository.child,
    api.logger,
  );
  service.class.inject(
    repository.classChild,
    api.logger,
  );
  service.temperature.inject(
    repository.temperature,
    api.logger,
  );
  service.temperatureNursery.inject(
    api.wagakoNurseryApi,
    api.logger,
  );
  service.attendContact.inject(
    api.wagakoNurseryApi,
    repository.absence,
    repository.late,
    repository.leaveEarly,
    api.logger,
  );
  service.attendRegister.inject(
    repository.attendRegister,
    api.logger,
  );
  service.childApply.inject(
    api.wagakoNurseryApi,
    repository.childApply,
    repository.childRemove,
    repository.facility,
    repository.homepageSettings,
    api.logger,
  );
  service.device.inject(
    repository.device,
    api.logger,
  );
  service.user.inject(
    api.cognitoApi,
    api.wagakoAppApi,
    api.wagakoNurseryApi,
    api.logger,
  );
  service.childNotebook.inject(
    repository.page,
    api.wagakoNurseryApi,
    api.logger,
  );
};
runInjection();
